import React, { useEffect } from 'react';

/**
 * Standard admin-panel modal.
 *
 * Uses the same structure the rest of the app uses (`.overlay` + `.common-popup`
 * → `.edit-heading` → `.edit-inner`), so it inherits the shared card, header
 * rule, scrolling body and close-button styling from main-style.css.
 *
 * Note `.common-popup` IS the dialog box, not a backdrop — the backdrop is the
 * separate `.overlay` element. Nesting a `.popup-content` inside it produces a
 * double card, which is what this component exists to prevent.
 */

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" aria-hidden="true">
        <path fill="#ffffff" d="M19.95 16.75l-.05-.4-1.2-1-5.2-4.2c-.1-.05-.3-.2-.6-.5l-.7-.55c-.15-.1-.5-.45-1-1.1l-.1-.1c.2-.15.4-.35.6-.55l1.95-1.85 1.1-1c1-1 1.7-1.65 2.1-1.9l.5-.35c.4-.25.65-.45.75-.45.2-.15.45-.35.65-.6s.3-.5.3-.7l-.3-.65c-.55.2-1.2.65-2.05 1.35-.85.75-1.65 1.55-2.5 2.5-.8.9-1.6 1.65-2.4 2.3-.8.65-1.4.95-1.9 1-.15 0-1.5-1.05-4.1-3.2C3.1 2.6 1.45 1.2.7.55L.45.1c-.1.05-.2.15-.3.3C.05.55 0 .7 0 .85l.05.35.05.4 1.2 1 5.2 4.15c.1.05.3.2.6.5l.7.6c.15.1.5.45 1 1.1l.1.1c-.2.15-.4.35-.6.55l-1.95 1.85-1.1 1c-1 1-1.7 1.65-2.1 1.9l-.5.35c-.4.25-.65.45-.75.45-.25.15-.45.35-.65.6-.15.3-.25.55-.25.75l.3.65c.55-.2 1.2-.65 2.05-1.35.85-.75 1.65-1.55 2.5-2.5.8-.9 1.6-1.65 2.4-2.3.8-.65 1.4-.95 1.9-1 .15 0 1.5 1.05 4.1 3.2 2.6 2.15 4.3 3.55 5.05 4.2l.2.45c.1-.05.2-.15.3-.3.1-.15.15-.3.15-.45z" />
    </svg>
);

const Popup = ({
    title,
    onClose,
    children,
    footer,
    maxWidth,
    icon,
    iconColor = 'var(--first-color)',
    className = '',
}) => {
    // Lock the page behind the modal, and close on Escape.
    useEffect(() => {
        document.body.classList.add('no_scroll');
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.classList.remove('no_scroll');
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [onClose]);

    return (
        <>
            <div className="overlay qv_active" onClick={onClose}></div>
            <div
                className={`common-popup active ${className}`.trim()}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
                style={maxWidth ? { maxWidth } : undefined}
            >
                <div className="edit-heading d-flex align-items-center justify-content-between">
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, fontSize: 18 }}>
                        {icon && <i className={icon} style={{ color: iconColor, fontSize: 16 }}></i>}
                        {title}
                    </h3>
                    <button type="button" className="common-close edit-close-btn" onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                <div className="edit-inner">
                    {children}
                    {footer && (
                        <div className="action-footer d-flex flex-wrap gap-2 justify-content-end">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Popup;
